import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '~/lib/api';

type Status =
  | { kind: 'verifying' }
  | { kind: 'ok' }
  | { kind: 'error'; message: string };

/**
 * Landing page for the ?token= link sent by LogMailer / Resend. The backend
 * sets the session cookie on success, so we just POST and navigate; the
 * dashboard's own /dashboard/me call rides the fresh cookie. Failure
 * (expired, already used, bad token) shows a short message with a path back
 * to /login.
 */
export function VerifyEmailPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [status, setStatus] = useState<Status>({ kind: 'verifying' });

  useEffect(() => {
    if (!token) {
      setStatus({ kind: 'error', message: 'This link is missing a token.' });
      return;
    }
    let active = true;
    (async () => {
      try {
        await api('/auth/verify-email', { method: 'POST', body: { token } });
        if (!active) return;
        setStatus({ kind: 'ok' });
        setTimeout(() => navigate('/', { replace: true }), 400);
      } catch (err) {
        if (!active) return;
        setStatus({
          kind: 'error',
          message:
            err instanceof ApiError
              ? err.message
              : 'We could not verify this link.',
        });
      }
    })();
    return () => {
      active = false;
    };
  }, [token, navigate]);

  return (
    <main
      data-ground="landing"
      className="grid min-h-full place-items-center bg-surface-canvas p-6"
    >
      <div className="w-full max-w-[400px]">
        <div className="mb-10 flex items-center gap-2.5">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent-wax text-[14px] font-semibold text-accent-wax-ink shadow-[inset_0_1px_0_rgba(255,255,255,.1)]">
            S
          </span>
          <span className="font-serif text-[22px] -tracking-[0.01em]">Seven</span>
        </div>

        {status.kind === 'verifying' && (
          <>
            <h1 className="mb-2 font-serif text-[32px] font-normal leading-[36px] -tracking-[0.02em]">
              Verifying…
            </h1>
            <p className="text-[14px] text-ink-secondary">
              One moment while we check your link.
            </p>
          </>
        )}

        {status.kind === 'ok' && (
          <>
            <h1 className="mb-2 font-serif text-[32px] font-normal leading-[36px] -tracking-[0.02em]">
              Signed <em className="italic text-accent-wax">in</em>
            </h1>
            <p className="text-[14px] text-ink-secondary">
              Taking you to your dashboard.
            </p>
          </>
        )}

        {status.kind === 'error' && (
          <>
            <h1 className="mb-2 font-serif text-[32px] font-normal leading-[36px] -tracking-[0.02em]">
              Link didn't work
            </h1>
            <p className="mb-6 text-[14px] text-ink-secondary">
              {status.message}
            </p>
            <Link
              to="/login"
              className="inline-flex h-9 items-center justify-center rounded-md bg-accent-wax px-3.5 text-[14.5px] font-semibold text-accent-wax-ink hover:bg-accent-wax-deep"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
